import { useState, useEffect } from 'react';

import { useEmbedding, EmbeddingState } from '@/components/embed-provider';
import { envUtils } from '@/lib/utils';
import {
  KIZEO_MESSAGE_TYPES,
  kizeoMessaging,
} from '@/app/routes/embed/kizeo/messaging';

const KIZEO_EMBED_CONFIG: EmbeddingState = {
  hideSideNav: true,
  prefix: '',
  hideLogoInBuilder: false,
  disableNavigationInBuilder: false,
  hideFolders: false,
  hideFlowNameInBuilder: false,
  isEmbedded: true,
  sdkVersion: undefined,
};

const mapKizeoLanguageCode = (kizeoLangCode: string | undefined): string => {
  if (!kizeoLangCode) return 'en';
  return kizeoLangCode === 'zh' ? 'zh-Hans' : kizeoLangCode;
};

export enum ValidationState {
  PENDING = 'pending',
  VALID = 'valid',
  INVALID = 'invalid',
}

const useValidateKizeoEmbed = () => {
  const [validation, setValidation] = useState<ValidationState>(
    ValidationState.PENDING,
  );

  const { setEmbedState } = useEmbedding();

  useEffect(() => {
    const kizeoOrigin = envUtils.getKizeoFormsUrl();

    if (!kizeoOrigin) {
      setValidation(ValidationState.INVALID);
      return;
    }

    kizeoMessaging.sendKhubRequestValidation();

    const timeout = setTimeout(() => {
      console.error('❌ Kizeo validation timeout - not in valid Kizeo iframe');
      setValidation(ValidationState.INVALID);
    }, 3000);

    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin === kizeoOrigin &&
        event.data?.type === KIZEO_MESSAGE_TYPES.KIZEO_EMBED_INIT
      ) {
        clearTimeout(timeout);
        window.removeEventListener('message', handleMessage);

        const targetRoute = event.data.data?.targetRoute || '/flows';
        const kizeoLanguage = event.data.data?.language;
        const language = mapKizeoLanguageCode(kizeoLanguage);

        setEmbedState({
          ...KIZEO_EMBED_CONFIG,
          kizeoEmbed: {
            isActive: true,
            targetRoute,
            language,
          },
        });

        setValidation(ValidationState.VALID);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('message', handleMessage);
    };
  }, [setEmbedState]);

  return validation;
};

export const embedHooks = {
  useValidateKizeoEmbed,
};
